import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING } from '../constants/theme';

const ChildManagementCard = ({ onClose, onChildrenChanged }) => {
  const { profile } = useAuth();
  const { isDesktop } = useResponsive();
  const [mode, setMode] = useState('list'); // 'list', 'add', 'edit'
  const [children, setChildren] = useState([]);
  const [teams, setTeams] = useState([]);
  const [editingChild, setEditingChild] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [childToDelete, setChildToDelete] = useState(null);

  const [formData, setFormData] = useState({
    full_name: '',
    birth_date: '',
    phone: '',
    team_id: null,
  });

  useEffect(() => {
    loadChildren();
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadChildren = async () => {
    try {
      // Build the query based on whether parent has a family_id
      let query = supabase.from('profiles').select('*, teams(name)');

      // If parent has family_id, include independent children (via family_id)
      // Always include dependent children (via managed_by)
      if (profile.family_id) {
        query = query.or(
          `and(family_id.eq.${profile.family_id},family_id.not.is.null),managed_by.eq.${profile.id}`
        );
      } else {
        // Parent has no family_id yet, only fetch dependent children
        query = query.eq('managed_by', profile.id);
      }

      const { data, error } = await query.neq('id', profile.id).order('full_name');

      if (error) throw error;
      setChildren(data || []);
    } catch (error) {
      console.error('Error loading children:', error);
      setError(error.message);
    }
  };

  const loadTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .eq('organization_id', profile.organization_id)
        .order('name');

      if (error) throw error;
      setTeams(data || []);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  const ensureParentHasFamilyId = async () => {
    // If parent doesn't have a family_id, generate one
    if (!profile.family_id) {
      const newFamilyId = crypto.randomUUID();
      const { error } = await supabase
        .from('profiles')
        .update({ family_id: newFamilyId })
        .eq('id', profile.id);

      if (error) throw error;

      // Update local profile (this is a simplification - in production you'd refresh AuthContext)
      profile.family_id = newFamilyId;
    }
  };

  const handleAdd = () => {
    setMode('add');
    setFormData({
      full_name: '',
      birth_date: '',
      phone: '',
      team_id: null,
    });
    setError('');
    setSuccess('');
  };

  const handleEdit = (child) => {
    // Only allow editing dependent children
    if (!child.is_dependent || child.managed_by !== profile.id) {
      setError('You can only edit dependent children that you manage.');
      return;
    }

    setMode('edit');
    setEditingChild(child);
    setFormData({
      full_name: child.full_name || '',
      birth_date: child.birth_date || '',
      phone: child.phone || '',
      team_id: child.team_id || null,
    });
    setError('');
    setSuccess('');
  };

  const handleDeleteClick = (child) => {
    // Only allow deleting dependent children
    if (!child.is_dependent || child.managed_by !== profile.id) {
      setError('You can only delete dependent children that you manage.');
      return;
    }

    setChildToDelete(child);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setError('');

      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', childToDelete.id)
        .eq('managed_by', profile.id); // Extra safety check

      if (error) throw error;

      setSuccess(`${childToDelete.full_name} has been removed.`);
      setShowDeleteConfirm(false);
      setChildToDelete(null);
      await loadChildren();
      onChildrenChanged(); // Notify parent component

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting child:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.full_name.trim()) {
      setError('Full name is required.');
      return;
    }

    if (formData.birth_date) {
      const birthDate = new Date(formData.birth_date);
      const today = new Date();
      if (birthDate > today) {
        setError('Birth date cannot be in the future.');
        return;
      }
    }

    try {
      setLoading(true);

      if (mode === 'add') {
        // Ensure parent has a family_id
        await ensureParentHasFamilyId();

        // Create dependent child
        const childData = {
          full_name: formData.full_name.trim(),
          birth_date: formData.birth_date || null,
          phone: formData.phone || null,
          team_id: formData.team_id || null,
          role: 'player',
          is_dependent: true,
          managed_by: profile.id,
          organization_id: profile.organization_id,
          family_id: profile.family_id,
          email: null, // Dependent children don't have email
          id: crypto.randomUUID(), // Generate UUID for dependent player
        };

        const { error } = await supabase.from('profiles').insert([childData]);

        if (error) throw error;

        setSuccess('Child added successfully!');
        setMode('list');
        await loadChildren();
        onChildrenChanged();
      } else if (mode === 'edit') {
        // Update existing child
        const updates = {
          full_name: formData.full_name.trim(),
          birth_date: formData.birth_date || null,
          phone: formData.phone || null,
          team_id: formData.team_id || null,
        };

        const { error } = await supabase
          .from('profiles')
          .update(updates)
          .eq('id', editingChild.id)
          .eq('managed_by', profile.id); // Extra safety check

        if (error) throw error;

        setSuccess('Child updated successfully!');
        setMode('list');
        setEditingChild(null);
        await loadChildren();
        onChildrenChanged();
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving child:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setMode('list');
    setEditingChild(null);
    setFormData({
      full_name: '',
      birth_date: '',
      phone: '',
      team_id: null,
    });
    setError('');
  };

  const styles = {
    container: {
      backgroundColor: COLORS.white,
      padding: isDesktop ? SPACING.xl : SPACING.lg,
      borderRadius: '8px',
      maxWidth: '800px',
      width: isDesktop ? '100%' : '95vw',
      maxHeight: '90vh',
      overflow: 'auto',
      boxSizing: 'border-box',
    },
    title: {
      marginTop: 0,
      marginBottom: SPACING.lg,
      fontSize: isDesktop ? '24px' : '20px',
      color: COLORS.textDark,
    },
    errorMessage: {
      padding: SPACING.md,
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      marginBottom: SPACING.md,
    },
    successMessage: {
      padding: SPACING.md,
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      marginBottom: SPACING.md,
    },
    childCard: {
      backgroundColor: COLORS.background,
      padding: SPACING.md,
      borderRadius: '8px',
      marginBottom: SPACING.md,
      border: `1px solid ${COLORS.border}`,
    },
    childName: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: SPACING.xs,
      color: COLORS.textDark,
    },
    childInfo: {
      fontSize: '14px',
      color: COLORS.textMuted,
      marginBottom: SPACING.xs,
    },
    badge: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      marginRight: SPACING.xs,
    },
    dependentBadge: {
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
    },
    independentBadge: {
      backgroundColor: '#e8f5e9',
      color: '#388e3c',
    },
    buttonGroup: {
      display: 'flex',
      gap: SPACING.sm,
      marginTop: SPACING.sm,
      flexWrap: 'wrap',
    },
    button: {
      minHeight: '44px',
      padding: `${SPACING.sm} ${SPACING.md}`,
      backgroundColor: COLORS.primary,
      color: COLORS.white,
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      fontWeight: '500',
    },
    secondaryButton: {
      minHeight: '44px',
      padding: `${SPACING.sm} ${SPACING.md}`,
      backgroundColor: COLORS.white,
      color: COLORS.primary,
      border: `2px solid ${COLORS.primary}`,
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      fontWeight: '500',
    },
    dangerButton: {
      minHeight: '44px',
      padding: `${SPACING.sm} ${SPACING.md}`,
      backgroundColor: '#d32f2f',
      color: COLORS.white,
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      fontWeight: '500',
    },
    formGroup: {
      marginBottom: SPACING.md,
    },
    label: {
      display: 'block',
      marginBottom: SPACING.xs,
      fontWeight: '600',
      fontSize: '16px',
      color: COLORS.textDark,
    },
    input: {
      width: '100%',
      minHeight: '44px',
      padding: SPACING.sm,
      fontSize: '16px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '4px',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      minHeight: '44px',
      padding: SPACING.sm,
      fontSize: '16px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '4px',
      backgroundColor: COLORS.white,
      boxSizing: 'border-box',
      cursor: 'pointer',
    },
    emptyState: {
      textAlign: 'center',
      padding: SPACING.xl,
      color: COLORS.textMuted,
    },
  };

  return (
    <div style={styles.container}>
      {/* List Mode */}
      {mode === 'list' && (
        <>
          <h2 style={styles.title}>Manage Children</h2>

          {error && <div style={styles.errorMessage}>{error}</div>}
          {success && <div style={styles.successMessage}>{success}</div>}

          {children.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No children found. Add your first child below.</p>
            </div>
          ) : (
            <div>
              {children.map((child) => (
                <div key={child.id} style={styles.childCard}>
                  <div style={styles.childName}>
                    {child.full_name}
                    <span
                      style={{
                        ...styles.badge,
                        ...(child.is_dependent ? styles.dependentBadge : styles.independentBadge),
                      }}
                    >
                      {child.is_dependent ? 'Managed' : 'Independent'}
                    </span>
                  </div>
                  {child.birth_date && (
                    <div style={styles.childInfo}>
                      Birth Date: {new Date(child.birth_date).toLocaleDateString()}
                    </div>
                  )}
                  {child.teams?.name && (
                    <div style={styles.childInfo}>Team: {child.teams.name}</div>
                  )}
                  {child.phone && <div style={styles.childInfo}>Phone: {child.phone}</div>}

                  {child.is_dependent && child.managed_by === profile.id && (
                    <div style={styles.buttonGroup}>
                      <button style={styles.secondaryButton} onClick={() => handleEdit(child)}>
                        Edit
                      </button>
                      <button style={styles.dangerButton} onClick={() => handleDeleteClick(child)}>
                        Delete
                      </button>
                    </div>
                  )}
                  {(!child.is_dependent || child.managed_by !== profile.id) && (
                    <div style={{ ...styles.childInfo, marginTop: SPACING.sm }}>
                      (Independent account - cannot be edited or deleted)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: SPACING.sm, marginTop: SPACING.lg }}>
            <button style={styles.button} onClick={handleAdd}>
              Add Player
            </button>
            <button style={styles.secondaryButton} onClick={onClose}>
              Close
            </button>
          </div>
        </>
      )}

      {/* Add/Edit Mode */}
      {(mode === 'add' || mode === 'edit') && (
        <>
          <h2 style={styles.title}>{mode === 'add' ? 'Add Player' : 'Edit Child'}</h2>

          {error && <div style={styles.errorMessage}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Full Name <span style={{ color: '#d32f2f' }}>*</span>
              </label>
              <input
                type="text"
                style={styles.input}
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                placeholder="Enter child's full name"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Birth Date (Optional)</label>
              <input
                type="date"
                style={styles.input}
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone (Optional)</label>
              <input
                type="tel"
                style={styles.input}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Team (Optional)</label>
              <select
                style={styles.select}
                value={formData.team_id || ''}
                onChange={(e) => setFormData({ ...formData, team_id: e.target.value || null })}
              >
                <option value="">No team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: SPACING.sm }}>
              <button
                type="submit"
                style={{ ...styles.button, opacity: loading ? 0.6 : 1 }}
                disabled={loading}
              >
                {loading ? 'Saving...' : mode === 'add' ? 'Add Child' : 'Save Changes'}
              </button>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && childToDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            style={{
              backgroundColor: COLORS.white,
              padding: SPACING.xl,
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Confirm Delete</h3>
            <p>
              Are you sure you want to delete <strong>{childToDelete.full_name}</strong>? This
              action cannot be undone.
            </p>
            {error && <div style={styles.errorMessage}>{error}</div>}
            <div style={{ display: 'flex', gap: SPACING.sm, justifyContent: 'flex-end' }}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setChildToDelete(null);
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.dangerButton, opacity: loading ? 0.6 : 1 }}
                onClick={handleDeleteConfirm}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildManagementCard;
